import {
  useMutation,
  type DefaultError,
  type UseMutationResult,
} from "@tanstack/react-query";
import {
  proposalsDeleteProposalCommissionMutation,
  useApi,
  useHeyApi,
  type BtxRequestOptions,
  type ProposalsDeleteProposalCommissionData,
  type ProposalsDeleteProposalCommissionResponse,
} from "@platform/api";
import { useCallback, useRef } from "react";

import {
  materializeProposalCommission,
  updateCompsColumnFreeText,
  updateProposalCommission,
  updateProposalFreeText,
  type CompsColumnKind,
  type MaterializeCommissionPayload,
  type UpdateProposalCommissionPayload,
  type UpdateProposalFreeTextPayload,
} from "./client";

import type { AxiosError } from "axios";

export interface UpdateProposalCommissionVariables {
  rfpId: string;
  proposalId: string;
  proposalPlanId: string;
  payload: UpdateProposalCommissionPayload;
}

export const useUpdateProposalCommission = (): ReturnType<
  typeof useMutation<void, Error, UpdateProposalCommissionVariables>
> => {
  const axios = useApi();

  return useMutation<void, Error, UpdateProposalCommissionVariables>({
    mutationFn: ({ rfpId, proposalId, proposalPlanId, payload }) =>
      updateProposalCommission(
        rfpId,
        proposalId,
        proposalPlanId,
        payload,
        axios,
      ),
  });
};

export const useDeleteProposalCommission = (): UseMutationResult<
  ProposalsDeleteProposalCommissionResponse,
  AxiosError<DefaultError>,
  BtxRequestOptions<ProposalsDeleteProposalCommissionData>
> => {
  const { btxClient } = useHeyApi();

  return useMutation(
    proposalsDeleteProposalCommissionMutation({ client: btxClient }),
  );
};

export interface UpdateProposalFreeTextVariables {
  rfpId: string;
  proposalId: string;
  payload: UpdateProposalFreeTextPayload;
}

export const useUpdateProposalFreeText = (): ReturnType<
  typeof useMutation<void, Error, UpdateProposalFreeTextVariables>
> => {
  const axios = useApi();

  return useMutation<void, Error, UpdateProposalFreeTextVariables>({
    mutationFn: ({ rfpId, proposalId, payload }) =>
      updateProposalFreeText(rfpId, proposalId, payload, axios),
  });
};

export interface UpdateCompsColumnFreeTextVariables {
  rfpId: string;
  columnKind: CompsColumnKind;
  payload: UpdateProposalFreeTextPayload;
}

export const useUpdateCompsColumnFreeText = (): ReturnType<
  typeof useMutation<void, Error, UpdateCompsColumnFreeTextVariables>
> => {
  const axios = useApi();

  return useMutation<void, Error, UpdateCompsColumnFreeTextVariables>({
    mutationFn: ({ rfpId, columnKind, payload }) =>
      updateCompsColumnFreeText(rfpId, columnKind, payload, axios),
  });
};

export interface UseMaterializeCompsCommissionOptions {
  rfpId: string;
  /**
   * Invalidates the Comps table query. Its returned promise is awaited before
   * the in-flight guard is released, so the placeholder cell is replaced by the
   * real proposal plan before another materialize can fire.
   */
  invalidate: () => Promise<unknown>;
  onError?: (error: Error) => void;
}

/** In-flight key for a materialize: one analyst plan per (carrier, plan group). */
const materializeKey = (payload: MaterializeCommissionPayload): string =>
  `${payload.carrierId}:${payload.rfpBenefitPlanGroupId}`;

/**
 * Materializes an analyst proposal plan + commission for an unquoted Comps cell
 * (ADR-024), guarding against duplicate submissions.
 *
 * The materialize endpoint always appends a new plan group (it is not idempotent
 * per plan group), and the request has real latency during which the edited cell
 * still shows the placeholder (no proposalPlanId yet). Without a guard, a second
 * edit in that window would materialize a duplicate plan. So a `(carrier, group)`
 * key stays in flight from submit until the Comps refetch replaces the
 * placeholder with the real column.
 */
export const useMaterializeCompsCommission = ({
  rfpId,
  invalidate,
  onError,
}: UseMaterializeCompsCommissionOptions): {
  materialize: (payload: MaterializeCommissionPayload) => void;
} => {
  const axios = useApi();
  const inFlightKeys = useRef(new Set<string>());

  const { mutate } = useMutation<void, Error, MaterializeCommissionPayload>({
    mutationFn: (payload) =>
      materializeProposalCommission(rfpId, payload, axios),
    // Await the refetch so the placeholder is replaced before the guard is
    // released (see onSettled) — otherwise a quick second edit would duplicate.
    onSuccess: () => invalidate(),
    onError: (error) => onError?.(error),
    onSettled: (_data, _error, payload) => {
      inFlightKeys.current.delete(materializeKey(payload));
    },
  });

  const materialize = useCallback(
    (payload: MaterializeCommissionPayload) => {
      const key = materializeKey(payload);
      if (inFlightKeys.current.has(key)) return;
      inFlightKeys.current.add(key);
      mutate(payload);
    },
    [mutate],
  );

  return { materialize };
};
